import { Link } from "react-router";
import { DitherFade, GlobeDots, HeroStripes } from "@/components/ui/decorative-patterns";
import { asset } from "@/lib/asset";

/**
 * Shared auth chrome for /login and /invite — ticketbalie house pattern in
 * Prado green, shared with the managing login as one hole.
 *
 * Desktop: dotted globe only; outer recessed muted frame holding a raised
 * white content card, with a gray footer strip below it for secondary routes.
 * Mobile: ribbon stripes up top + dither rising from the bottom edge, chrome
 * dropped away so title + form sit directly on the page.
 * UI-only: same API shape (`children` + `footer`), no logic changes.
 */
export function AuthLayout({
  children,
  footer,
}: {
  children: React.ReactNode;
  /** Secondary links shown in the recessed gray strip under the card. */
  footer?: React.ReactNode;
}) {
  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-x-clip bg-background px-5 py-10">
      {/* Desktop backdrop: the dotted globe. No top dither. */}
      <div className="hidden md:block">
        <GlobeDots />
      </div>
      {/* Mobile backdrop: ribbon stripes up top, dither from the bottom. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden md:hidden">
        <HeroStripes />
        <DitherFade anchor="bottom" className="h-[380px]" />
      </div>

      <Link
        to="/"
        className="fixed left-4 top-4 z-30 inline-flex items-center gap-2"
        aria-label="Prado home"
      >
        <img
          src={asset("prado-logo.png")}
          alt="Prado"
          className="h-7 w-auto select-none object-contain dark:invert dark:opacity-90 sm:h-8"
          draggable={false}
        />
      </Link>

      <div className="relative z-10 flex w-full flex-col items-center">
        <div className="w-full max-w-sm sm:max-w-md sm:overflow-hidden sm:rounded-[20px] sm:border sm:border-border/60 sm:bg-[hsl(140_14%_97%)] dark:sm:bg-[hsl(140_8%_9%)]">
          {/* Raised content card */}
          <div className="sm:rounded-[18px] sm:border sm:border-border/60 sm:bg-card sm:shadow-[0_12px_40px_rgba(0,0,0,0.08)]">
            <div className="sm:px-8 sm:pb-8 sm:pt-2">{children}</div>
          </div>
          {/* Gray footer strip — secondary routes live here, not in the card */}
          {footer && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm sm:mt-0 sm:px-6 sm:py-4">
              {footer}
            </div>
          )}
        </div>
        <p className="mt-8 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Prado · Marketing Intelligence
        </p>
      </div>
    </div>
  );
}

/** Dot separator between footer links, matching the sign-in footer rhythm. */
export function AuthFooterDot() {
  return (
    <span aria-hidden className="text-muted-foreground/50">
      ·
    </span>
  );
}
